import React, { useState, useEffect, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─── Helpers ───────────────────────────────────────────────────────────────

const ACTION_LABELS = {
  APPROVE_APPLICATION_NEW: "Approve (New)",
  APPROVE_APPLICATION_RENEW: "Approve (Renewal)",
  REJECT_APPLICATION_NEW: "Reject (New)",
  REJECT_APPLICATION_RENEW: "Reject (Renewal)",
  DELETE_APPLICATION: "Delete Application",
  CHANGE_EXPIRY_DATE: "Change Expiry Date",
  LIST_APPLICATIONS: "List Applications",
};

const ACTION_COLORS = {
  APPROVE_APPLICATION_NEW: "#22c55e",
  APPROVE_APPLICATION_RENEW: "#22c55e",
  REJECT_APPLICATION_NEW: "#ef4444",
  REJECT_APPLICATION_RENEW: "#ef4444",
  DELETE_APPLICATION: "#dc2626",
  CHANGE_EXPIRY_DATE: "#f59e0b",
  LIST_APPLICATIONS: "#6366f1",
};

/** Actions available per application status — mirrors backend VALID_ACTIONS_BY_STATUS */
const STATUS_ACTIONS = {
  under_verification: ["APPROVE_APPLICATION_NEW", "REJECT_APPLICATION_NEW", "DELETE_APPLICATION"],
  pending: ["APPROVE_APPLICATION_NEW", "REJECT_APPLICATION_NEW", "DELETE_APPLICATION"],
  under_renewal: ["APPROVE_APPLICATION_RENEW", "REJECT_APPLICATION_RENEW", "DELETE_APPLICATION"],
  approved: ["CHANGE_EXPIRY_DATE", "DELETE_APPLICATION"],
  rejected: ["DELETE_APPLICATION"],
  replaced: ["DELETE_APPLICATION"],
};

const STATUS_COLORS = {
  approved: "#22c55e",
  approved_can_renew: "#84cc16",
  approved_no_renew: "#22c55e",
  under_verification: "#f59e0b",
  under_renewal: "#a78bfa",
  pending: "#f59e0b",
  rejected: "#ef4444",
  replaced: "#94a3b8",
};

// ─── Inline Styles ──────────────────────────────────────────────────────────

const styles = {
  fab: {
    position: "fixed",
    bottom: "28px",
    right: "28px",
    zIndex: 9999,
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
  },
  trigger: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "linear-gradient(135deg, #1e293b 0%, #334155 100%)",
    border: "2px solid rgba(245,158,11,0.7)",
    boxShadow: "0 8px 32px rgba(0,0,0,0.45), 0 0 0 0 rgba(245,158,11,0.4)",
    color: "#fff",
    fontSize: "22px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "transform 0.2s, box-shadow 0.2s",
    animation: "demo-pulse 2.5s infinite",
  },
  panel: {
    position: "absolute",
    bottom: "70px",
    right: "0",
    width: "360px",
    maxHeight: "80vh",
    overflowY: "auto",
    background: "#0f172a",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,0.08)",
    boxShadow: "0 24px 64px rgba(0,0,0,0.6), 0 0 0 1px rgba(245,158,11,0.15)",
    color: "#e2e8f0",
    animation: "demo-slide-up 0.22s cubic-bezier(0.34,1.56,0.64,1)",
  },
  panelHeader: {
    padding: "14px 18px",
    background: "linear-gradient(135deg, #1e293b, #0f172a)",
    borderBottom: "1px solid rgba(255,255,255,0.07)",
    borderRadius: "16px 16px 0 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  badge: {
    background: "rgba(245,158,11,0.15)",
    border: "1px solid rgba(245,158,11,0.4)",
    color: "#fbbf24",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    padding: "2px 8px",
    borderRadius: "999px",
    textTransform: "uppercase",
  },
  section: {
    padding: "12px 14px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  sectionTitle: {
    fontSize: "11px",
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: "#64748b",
    textTransform: "uppercase",
    marginBottom: "10px",
  },
  appCard: {
    background: "rgba(255,255,255,0.04)",
    borderRadius: "10px",
    padding: "10px 12px",
    marginBottom: "8px",
    border: "1px solid rgba(255,255,255,0.07)",
    cursor: "pointer",
    transition: "background 0.15s",
  },
  appCardSelected: {
    background: "rgba(99,102,241,0.15)",
    border: "1px solid rgba(99,102,241,0.4)",
  },
  appNum: {
    fontFamily: "monospace",
    fontSize: "13px",
    fontWeight: 600,
    color: "#f1f5f9",
  },
  appMeta: {
    fontSize: "11px",
    color: "#64748b",
    marginTop: "2px",
  },
  statusDot: (color) => ({
    display: "inline-block",
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    background: color,
    marginRight: "6px",
    verticalAlign: "middle",
  }),
  statusText: (color) => ({
    color: color,
    fontSize: "12px",
    fontWeight: 600,
  }),
  actionBtn: (color, disabled) => ({
    width: "100%",
    padding: "9px 12px",
    borderRadius: "8px",
    border: `1px solid ${color}40`,
    background: `${color}14`,
    color: color,
    fontSize: "13px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.5 : 1,
    textAlign: "left",
    marginBottom: "6px",
    transition: "background 0.15s, transform 0.1s",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  }),
  input: {
    width: "100%",
    padding: "8px 10px",
    borderRadius: "8px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.06)",
    color: "#f1f5f9",
    fontSize: "13px",
    outline: "none",
    marginTop: "6px",
    boxSizing: "border-box",
    colorScheme: "dark",
  },
  toast: (success) => ({
    position: "fixed",
    bottom: "100px",
    right: "28px",
    background: success ? "#064e3b" : "#450a0a",
    border: `1px solid ${success ? "#22c55e" : "#ef4444"}40`,
    color: success ? "#86efac" : "#fca5a5",
    padding: "10px 16px",
    borderRadius: "10px",
    fontSize: "13px",
    fontWeight: 600,
    zIndex: 10000,
    boxShadow: "0 8px 24px rgba(0,0,0,0.4)",
    animation: "demo-slide-up 0.2s ease",
    maxWidth: "320px",
  }),
};

// ─── Main Component ─────────────────────────────────────────────────────────

/**
 * DemoToolWidget
 *
 * @param {string} context  - "pending" | "approved" | "renewal" | null (shows all)
 * @param {object} applicationData - The current page's application (optional)
 */
export default function DemoToolWidget({ context = null, applicationData = null }) {
  const [enabled, setEnabled] = useState(false);
  const [allowedActions, setAllowedActions] = useState([]);
  const [open, setOpen] = useState(false);
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [loadingApps, setLoadingApps] = useState(false);
  const [expiryDate, setExpiryDate] = useState("");
  const [executing, setExecuting] = useState(null);
  const [toast, setToast] = useState(null);

  // ── Bootstrap: check availability ──────────────────────────────────────
  useEffect(() => {
    fetch(`${BACKEND_URL}/demo-tool`, { credentials: "include" })
      .then((r) => r.json())
      .then((d) => {
        if (d.enabled) {
          setEnabled(true);
          setAllowedActions(d.allowedActions || []);
        }
      })
      .catch(() => { });
  }, []);

  // ── On panel open: pre-select if context gives us an app, else list all ─
  useEffect(() => {
    if (!open || !enabled) return;

    // Pre-select current page's application
    if (applicationData?.application_number) {
      setSelectedApp(applicationData);
    }

    // Always fetch the full user list so they can switch
    setLoadingApps(true);
    fetch(`${BACKEND_URL}/demo-tool`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ action: "LIST_APPLICATIONS" }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) setApplications(d.data || []);
      })
      .catch(() => { })
      .finally(() => setLoadingApps(false));
  }, [open, enabled, applicationData]);

  // ── Derive which actions to show based on the SELECTED app's real status ─
  const relevantActions = (() => {
    if (!selectedApp) return [];
    const statusKey = selectedApp.status;
    const validForStatus = STATUS_ACTIONS[statusKey] || [];
    // Only show actions that are also enabled in the env
    return allowedActions.filter(
      (a) => a !== "LIST_APPLICATIONS" && validForStatus.includes(a)
    );
  })();

  // ── Toast helper ──────────────────────────────────────────────────────
  const showToast = (msg, success = true) => {
    setToast({ msg, success });
    setTimeout(() => setToast(null), 3500);
  };

  // ── Execute action ────────────────────────────────────────────────────
  const executeAction = useCallback(
    async (action) => {
      if (!selectedApp) return;
      setExecuting(action);

      let data;
      if (action === "CHANGE_EXPIRY_DATE") {
        if (!expiryDate) {
          showToast("Please select an expiry date first.", false);
          setExecuting(null);
          return;
        }
        data = { applicationNumber: selectedApp.application_number, date: expiryDate };
      } else {
        data = selectedApp.application_number;
      }

      try {
        const res = await fetch(`${BACKEND_URL}/demo-tool`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ action, data }),
        });
        const result = await res.json();
        if (result.success) {
          showToast(`✓ ${ACTION_LABELS[action]} completed!`, true);
          setOpen(false);
          // Redirect back to citizen portal to re-resolve status
          setTimeout(() => {
            window.location.href = "/passport/application";
          }, 1200);
        } else {
          showToast(result.message || "Action failed.", false);
        }
      } catch (e) {
        showToast("Network error. Try again.", false);
      } finally {
        setExecuting(null);
      }
    },
    [selectedApp, expiryDate]
  );

  if (!enabled) return null;

  // ── FAB icon (rotates when open) ──────────────────────────────────────
  const iconStyle = {
    ...styles.trigger,
    transform: open ? "rotate(45deg) scale(1.05)" : "scale(1)",
  };

  return (
    <>
      {/* CSS keyframes injected once */}
      <style>{`
        @keyframes demo-pulse {
          0%, 100% { box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 0 0 rgba(245,158,11,0.4); }
          50% { box-shadow: 0 8px 32px rgba(0,0,0,0.45), 0 0 0 8px rgba(245,158,11,0); }
        }
        @keyframes demo-slide-up {
          from { opacity: 0; transform: translateY(16px) scale(0.95); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .demo-app-card:hover { background: rgba(255,255,255,0.08) !important; }
        .demo-action-btn:hover:not(:disabled) { filter: brightness(1.2); transform: translateY(-1px); }
      `}</style>

      <div style={styles.fab}>
        {/* ── Panel ─────────────────────────────────────────────────────── */}
        {open && (
          <div style={styles.panel}>
            {/* Header */}
            <div style={styles.panelHeader}>
              <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                <span style={{ fontSize: "18px" }}>🛠️</span>
                <span style={{ fontWeight: 700, fontSize: "14px", color: "#f1f5f9" }}>Demo Tool</span>
              </div>
              <span style={styles.badge}>Demo Mode</span>
            </div>

            {/* Application list */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>Your Applications</div>
              {loadingApps ? (
                <div style={{ color: "#64748b", fontSize: "13px", textAlign: "center", padding: "10px 0" }}>
                  Loading…
                </div>
              ) : applications.length === 0 ? (
                <div style={{ color: "#64748b", fontSize: "13px" }}>No applications found.</div>
              ) : (
                applications.map((app) => {
                  const isSelected = selectedApp?.application_number === app.application_number;
                  const statusColor = STATUS_COLORS[app.status] || "#94a3b8";
                  return (
                    <div
                      key={app.application_number}
                      className="demo-app-card"
                      style={{ ...styles.appCard, ...(isSelected ? styles.appCardSelected : {}) }}
                      onClick={() => setSelectedApp(app)}
                    >
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={styles.appNum}>{app.application_number}</span>
                        <span>
                          <span style={styles.statusDot(statusColor)} />
                          <span style={styles.statusText(statusColor)}>
                            {app.status?.replace(/_/g, " ")}
                          </span>
                        </span>
                      </div>
                      <div style={styles.appMeta}>
                        {app.service_type?.toUpperCase()} &middot;{" "}
                        {app.submitted_at
                          ? new Date(app.submitted_at).toLocaleDateString()
                          : "N/A"}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Actions */}
            <div style={styles.section}>
              <div style={styles.sectionTitle}>
                Actions
                {selectedApp && (
                  <span style={{ color: "#6366f1", marginLeft: "6px", fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
                    — {selectedApp.application_number}
                  </span>
                )}
              </div>

              {!selectedApp ? (
                <div style={{ color: "#475569", fontSize: "12px", fontStyle: "italic" }}>
                  Select an application above to see available actions.
                </div>
              ) : relevantActions.length === 0 ? (
                <div style={{ color: "#475569", fontSize: "12px", fontStyle: "italic" }}>
                  No actions available for a <strong style={{ color: STATUS_COLORS[selectedApp.status] || "#94a3b8" }}>
                    {selectedApp.status?.replace(/_/g, " ")}
                  </strong> application.
                </div>
              ) : (
                <>

                  {relevantActions.map((action) => {
                    const color = ACTION_COLORS[action] || "#94a3b8";
                    const isRunning = executing === action;
                    const isDisabled = !!executing;

                    // If it's Change Expiry Date, show input along with the button
                    if (action === "CHANGE_EXPIRY_DATE") {
                      return (
                        <div key={action} style={{ marginBottom: "12px", border: `1px solid ${color}30`, borderRadius: "10px", padding: "8px", background: "rgba(255,255,255,0.02)" }}>
                          <div style={{ marginBottom: "8px" }}>
                            <label style={{ fontSize: "11px", color: "#94a3b8", display: "block", marginBottom: "4px" }}>Set New Expiry Date</label>
                            <input
                              type="date"
                              style={{ ...styles.input, marginTop: 0 }}
                              value={expiryDate}
                              min={new Date().toISOString().split("T")[0]}
                              onChange={(e) => setExpiryDate(e.target.value)}
                            />
                          </div>
                          <button
                            className="demo-action-btn"
                            style={styles.actionBtn(color, isDisabled)}
                            disabled={isDisabled}
                            onClick={() => executeAction(action)}
                          >
                            {isRunning ? "⏳" : getActionIcon(action)}
                            {isRunning ? "Running…" : ACTION_LABELS[action]}
                          </button>
                        </div>
                      );
                    }

                    return (
                      <button
                        key={action}
                        className="demo-action-btn"
                        style={styles.actionBtn(color, isDisabled)}
                        disabled={isDisabled}
                        onClick={() => executeAction(action)}
                      >
                        {isRunning ? "⏳" : getActionIcon(action)}
                        {isRunning ? "Running…" : ACTION_LABELS[action]}
                      </button>
                    );
                  })}
                </>
              )}
            </div>

            {/* Footer note */}
            <div style={{ padding: "10px 14px", fontSize: "11px", color: "#475569", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
              ⚠️ Demo mode — actions directly modify the records for testing purposes.
            </div>
          </div>
        )}

        {/* ── FAB Button ───────────────────────────────────────────────── */}
        <button
          id="demo-tool-fab"
          style={iconStyle}
          title="Demo Tool"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? "✕" : "🛠"}
        </button>
      </div>

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && <div style={styles.toast(toast.success)}>{toast.msg}</div>}
    </>
  );
}

function getActionIcon(action) {
  const icons = {
    APPROVE_APPLICATION_NEW: "✅ ",
    APPROVE_APPLICATION_RENEW: "✅ ",
    REJECT_APPLICATION_NEW: "❌ ",
    REJECT_APPLICATION_RENEW: "❌ ",
    DELETE_APPLICATION: "🗑️ ",
    CHANGE_EXPIRY_DATE: "📅 ",
  };
  return icons[action] || "▶️ ";
}
